import {InstallerFinance} from "../components/InstallerFinance";
// External finance: live recorded payments and dated published statements.
// Running balance/rate-card examples below remain mock-only. An internal
// crew gets the empty state: Team Artemis is paid through payroll.
//
// "Viewing as" is a review fixture, not a control: it exists only in mock mode
// and swaps the example data shown. On live, the role from /api/me decides and
// an internal crew never sees a rate.
import { useState } from "react";
import { CircleDollarSign } from "lucide-react";
import { cn } from "@aiw/ui";
import { useT } from "../i18n";
import { usePageHead } from "../shell/Frame";
import { useRole } from "../hooks/useMe";
import { useMyJobs, useStatement } from "../hooks/useReads";
import { env } from "../lib/env";
import { aud, day, isSameDay } from "../lib/format";
import { installationComplete, type Statement as StatementRow } from "../lib/types";
import { statement as exampleStatement } from "../mock/fixtures";
import { KV, Panel, Pill } from "../components/Panel";

export function Statement() {
  const { t, lang } = useT();
  const { isExternal, partyName } = useRole();
  const live = useStatement(env.mock);
  const jobs = useMyJobs();
  const [viewAs, setViewAs] = useState<"external" | "internal">(isExternal ? "external" : "internal");
  usePageHead(t(!env.mock&&isExternal?"payments_and_statements":"statement_title"));

  const showExternal = env.mock ? viewAs === "external" : isExternal;
  const data: StatementRow | null = showExternal ? (env.mock && !isExternal ? exampleStatement : live.data ?? null) : null;

  return (
    <div className="space-y-4">
      {env.mock && (
        <div className="flex flex-wrap items-center gap-2 rounded-md border border-dashed bg-muted/50 px-3 py-2 text-micro">
          <span><b>{t("viewing_as")}</b> · {t("fixture_note")}</span>
          <span className="ml-auto inline-flex overflow-hidden rounded-md border" role="group" aria-label={t("viewing_as")}>
            {(["external", "internal"] as const).map((v) => (
              <button key={v} type="button" onClick={() => setViewAs(v)} className={cn("min-h-[36px] px-3", viewAs === v ? "bg-rail text-rail-foreground" : "bg-background text-muted-foreground")}>
                {v === "external" ? `YU Flooring · ${t("view_external")}` : `Team Artemis · ${t("view_internal")}`}
              </button>
            ))}
          </span>
        </div>
      )}

      {showExternal ? (
        !env.mock ? <InstallerFinance/> : data ? <External data={data} /> : <p className="text-sm text-muted-foreground">{live.isLoading ? t("loading") : t("statement_empty")}</p>
      ) : (
        <div className="grid gap-4 md:grid-cols-2 md:items-start">
          <div className="space-y-4">
            <Panel>
              <p className="font-display text-3xl font-semibold">{(jobs.data ?? []).filter((j) => !installationComplete(j)).length}</p>
              <p className="text-sm text-muted-foreground">{t("int_stat", { n: (jobs.data ?? []).filter((j) => !installationComplete(j)).length })}</p>
            </Panel>
            <Panel title={t("all_crew_jobs")} bodyClassName="p-0">
              <ul className="divide-y text-sm">
                {(jobs.data ?? []).map((j) => (
                  <li key={j.ref} className="flex items-center justify-between gap-3 px-4 py-2.5">
                    <span className="min-w-0"><b>{j.name}</b><span className="block font-mono text-micro text-muted-foreground">{j.ref}</span></span>
                    <span className="shrink-0 text-right"><span className="block text-micro text-muted-foreground">{day(j.install_next_date ?? j.signed_off_at ?? null, lang)}</span>
                      {installationComplete(j) ? <Pill tone="ok">{j.signed_off_at ? t("pill_done", { date: day(j.signed_off_at, lang) }) : t("pill_install_complete")}</Pill> : isSameDay(j.install_next_date) ? <Pill tone="info">{t("pill_day_today", { n: 1 })}</Pill> : j.portal_state === "offered" ? <Pill tone="plain">{t("pill_offered", { when: day(j.answer_by, lang) })}</Pill> : <Pill tone="ok">{t("pill_scheduled")}</Pill>}
                    </span>
                  </li>
                ))}
              </ul>
            </Panel>
          </div>
          <div className="flex items-start gap-3 rounded-lg border border-dashed px-4 py-4">
            <span className="grid h-9 w-9 shrink-0 place-items-center rounded-full bg-muted text-muted-foreground"><CircleDollarSign className="h-4 w-4" aria-hidden /></span>
            <div className="text-sm"><b>{t("no_owed_title")}</b><p className="text-muted-foreground">{t("no_owed_body")}</p></div>
          </div>
          {!env.mock && <p className="sr-only">{partyName}</p>}
        </div>
      )}
    </div>
  );
}

function External({ data }: { data: StatementRow }) {
  const { t, lang } = useT();
  let running = 0;
  const lines = data.lines.map((l) => { running += l.amount; return { ...l, balance: running }; });
  return (
    <div className="grid gap-4 md:grid-cols-2 md:items-start">
      <div className="space-y-4">
        <Panel className="border-t-4 border-t-rail">
          <p className="num font-display text-3xl font-semibold text-foreground">{aud(data.owed)}</p>
          <p className="text-sm text-muted-foreground">{t("owed_to", { party: data.party.name, month: lang === "zh" ? data.month_zh : data.month })}</p>
          <div className="mt-3 flex flex-wrap gap-2">
            {data.paid && <Pill tone="ok">{t("paid_on", { amount: aud(data.paid.amount), date: day(data.paid.at, lang) })}</Pill>}
            {data.pending > 0 && <Pill tone="info">{t("pending_signoff", { amount: aud(data.pending) })}</Pill>}
          </div>
        </Panel>
        <Panel title={t("lines")} right={<span>{t("your_labour_only")}</span>} bodyClassName="p-0">
          <div className="grid grid-cols-[1fr_auto_auto] gap-x-3 border-b px-4 py-1.5 text-micro text-muted-foreground"><span>{t("col_job")} · {t("col_work")}</span><span className="text-right">{t("col_amount")}</span><span className="text-right">{t("col_balance")}</span></div>
          <ul className="divide-y text-sm">
            {lines.map((l) => (
              <li key={`${l.job_ref}-${l.work}`} className="grid grid-cols-[1fr_auto_auto] items-center gap-x-3 px-4 py-2.5">
                <span className="min-w-0"><b>{l.job}</b><span className="block text-micro text-muted-foreground">{l.work} · <span className="font-mono">{l.job_ref}</span></span><span className="mt-1 block"><Pill tone={l.state === "paid" ? "ok" : "info"}>{l.state === "paid" ? t("state_paid") : t("state_pending")}</Pill></span></span>
                <span className="num text-right text-foreground">{aud(l.amount)}</span>
                <span className="num text-right text-muted-foreground">{aud(l.balance)}</span>
              </li>
            ))}
          </ul>
        </Panel>
      </div>
      <div className="space-y-4">
        <Panel title={t("statements")} bodyClassName="p-0">
          <ul className="divide-y text-sm">
            {data.statements.map((s) => (
              <li key={s.id} className="flex min-h-[44px] items-center justify-between gap-3 px-4 py-2">
                <span className="font-mono">{s.id}{s.draft ? ` · ${t("draft")}` : s.total != null ? ` · ${aud(s.total)}` : ""}</span>
                <span className="text-micro text-muted-foreground">PDF</span>
              </li>
            ))}
          </ul>
        </Panel>
        <Panel title={t("rate_card")}>
          <KV rows={data.rates.map((r) => ({ k: t(r.key), v: <span className="num">{r.rate}{r.per_step ? ` ${t("per_step")}` : ""}{r.agreed ? <span className="text-muted-foreground"> · {t("agreed", { when: r.agreed })}</span> : null}</span> }))} />
          <p className="mt-3 text-micro text-muted-foreground">{t("rates_never")}</p>
        </Panel>
      </div>
    </div>
  );
}
